import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import type {
  ForgotPasswordInput,
  LoginInput,
  LoginResponse,
  MfaChallengeInput,
  ResetPasswordInput,
  SessionResponse,
} from '@orion/contracts';
import { api, setCsrfToken, ApiError } from './api-client.js';

export const sessionQueryOptions = queryOptions({
  queryKey: ['session'],
  queryFn: () => api.get<SessionResponse>('/auth/session'),
  retry: false,
  staleTime: 60_000,
});

interface AuthContextValue {
  session?: SessionResponse;
  isLoading: boolean;
  hasPermission: (key: string) => boolean;
  login: (input: LoginInput) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  acceptInvitation: (input: ResetPasswordInput) => Promise<void>;
  verifyMfaChallenge: (input: MfaChallengeInput) => Promise<SessionResponse>;
  requestPasswordReset: (input: ForgotPasswordInput) => Promise<void>;
  resetPassword: (input: ResetPasswordInput) => Promise<void>;
  loginError?: string;
  acceptInvitationError?: string;
  mfaVerifyError?: string;
  resetPasswordError?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({ ...sessionQueryOptions, throwOnError: false });

  useEffect(() => {
    setCsrfToken(sessionQuery.data?.csrfToken ?? null);
  }, [sessionQuery.data?.csrfToken]);

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => api.post<LoginResponse>('/auth/login', input),
    onSuccess: (result) => {
      if ('mfaRequired' in result) return;
      setCsrfToken(result.csrfToken);
      queryClient.setQueryData(sessionQueryOptions.queryKey, result);
    },
  });

  const mfaVerifyMutation = useMutation({
    mutationFn: (input: MfaChallengeInput) => api.post<SessionResponse>('/auth/mfa/verify', input),
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(sessionQueryOptions.queryKey, session);
    },
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: (input: ResetPasswordInput) => api.post<SessionResponse>('/auth/invitations/accept', input),
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(sessionQueryOptions.queryKey, session);
    },
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: (input: ForgotPasswordInput) => api.post<void>('/auth/forgot-password', input),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (input: ResetPasswordInput) => api.post<SessionResponse>('/auth/reset-password', input),
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(sessionQueryOptions.queryKey, session);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      setCsrfToken(null);
      queryClient.setQueryData(sessionQueryOptions.queryKey, undefined);
      queryClient.invalidateQueries();
    },
  });

  const loginErrorMessage =
    loginMutation.error instanceof ApiError
      ? (loginMutation.error.problem.detail ?? loginMutation.error.problem.title)
      : undefined;
  const acceptInvitationErrorMessage =
    acceptInvitationMutation.error instanceof ApiError
      ? (acceptInvitationMutation.error.problem.detail ?? acceptInvitationMutation.error.problem.title)
      : undefined;
  const mfaVerifyErrorMessage =
    mfaVerifyMutation.error instanceof ApiError
      ? (mfaVerifyMutation.error.problem.detail ?? mfaVerifyMutation.error.problem.title)
      : undefined;
  const resetPasswordErrorMessage =
    resetPasswordMutation.error instanceof ApiError
      ? (resetPasswordMutation.error.problem.detail ?? resetPasswordMutation.error.problem.title)
      : undefined;

  const value: AuthContextValue = {
    session: sessionQuery.data,
    isLoading: sessionQuery.isLoading,
    hasPermission: (key) => sessionQuery.data?.permissions.includes(key) ?? false,
    login: (input) => loginMutation.mutateAsync(input),
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    acceptInvitation: async (input) => {
      await acceptInvitationMutation.mutateAsync(input);
    },
    verifyMfaChallenge: (input) => mfaVerifyMutation.mutateAsync(input),
    requestPasswordReset: async (input) => {
      await requestPasswordResetMutation.mutateAsync(input);
    },
    resetPassword: async (input) => {
      await resetPasswordMutation.mutateAsync(input);
    },
    loginError: loginErrorMessage,
    acceptInvitationError: acceptInvitationErrorMessage,
    mfaVerifyError: mfaVerifyErrorMessage,
    resetPasswordError: resetPasswordErrorMessage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
